import type { ApiErrorBody } from './types';

// Centralized fetch wrapper.
//
// - Attaches the in-memory access token via `Authorization: Bearer <token>`.
// - Sends `credentials: 'include'` so the httpOnly refresh cookie is carried.
// - On a 401 from the access token, transparently calls /api/auth/refresh and
//   replays the original request once. If refresh fails we clear the access
//   token, broadcast a sign-out event, and re-throw the original 401.
// - Surface errors as `ApiError` instances carrying the backend's
//   `{ code, message }` so UI code branches on the symbolic code.

export class ApiError extends Error {
    code: string;
    status: number;
    body?: unknown;
    constructor(status: number, code: string, message: string, body?: unknown) {
        super(message);
        this.status = status;
        this.code = code;
        this.body = body;
    }
}

let accessToken: string | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let onSignedOut: (() => void) | null = null;

export function setAccessToken(tok: string | null) {
    accessToken = tok;
}

export function getAccessToken(): string | null {
    return accessToken;
}

// Registered by AuthProvider so the fetch wrapper can call back into the auth
// layer for a refresh without importing it (avoids a circular dependency).
export function registerRefreshHandler(h: (() => Promise<string | null>) | null) {
    refreshHandler = h;
}

export function registerSignedOutHandler(h: (() => void) | null) {
    onSignedOut = h;
}

interface ApiFetchOpts {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?:   unknown;          // JSON body
    form?:   FormData;         // multipart body (mutually exclusive with body)
    headers?: Record<string, string>;
    signal?:  AbortSignal;
    noAuth?:  boolean;          // skip Authorization header (login/refresh)
    noRefreshRetry?: boolean;   // internal: don't recurse on /auth/refresh
}

async function rawFetch(path: string, opts: ApiFetchOpts = {}): Promise<Response> {
    const headers: Record<string, string> = { ...(opts.headers || {}) };
    if (!opts.form && opts.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    if (!opts.noAuth && accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const init: RequestInit = {
        method:      opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET'),
        credentials: 'include',
        headers,
        signal:      opts.signal,
    };
    if (opts.form) {
        init.body = opts.form;
    } else if (opts.body !== undefined) {
        init.body = JSON.stringify(opts.body);
    }

    return fetch(path, init);
}

export async function apiFetch<T>(path: string, opts: ApiFetchOpts = {}): Promise<T> {
    let res = await rawFetch(path, opts);

    if (res.status === 401 && !opts.noAuth && !opts.noRefreshRetry && refreshHandler) {
        const newTok = await refreshHandler();
        if (newTok) {
            res = await rawFetch(path, { ...opts, noRefreshRetry: true });
        }
    }

    return interpret<T>(res);
}

async function interpret<T>(res: Response): Promise<T> {
    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const isPdf  = contentType.includes('application/pdf');

    if (!res.ok) {
        if (res.status === 401 && onSignedOut) onSignedOut();
        if (isJson) {
            const body = await res.json().catch(() => null) as ApiErrorBody | null;
            const code = body?.error?.code || 'HttpError';
            const msg  = body?.error?.message || res.statusText || 'Request failed';
            throw new ApiError(res.status, code, msg, body);
        }
        throw new ApiError(res.status, 'HttpError', res.statusText || 'Request failed');
    }

    if (isJson) return res.json() as Promise<T>;
    if (isPdf)  return res.blob() as unknown as Promise<T>;
    // Plain text fallback for the rare case
    return res.text() as unknown as Promise<T>;
}

// Convenience helper for endpoints that stream a PDF — returns the Blob and a
// suggested filename pulled from Content-Disposition.
export async function apiFetchBlob(path: string, opts: ApiFetchOpts = {}): Promise<{ blob: Blob; filename: string | null }> {
    let res = await rawFetch(path, opts);
    if (res.status === 401 && !opts.noAuth && !opts.noRefreshRetry && refreshHandler) {
        const newTok = await refreshHandler();
        if (newTok) res = await rawFetch(path, { ...opts, noRefreshRetry: true });
    }
    if (!res.ok) {
        const isJson = (res.headers.get('content-type') || '').includes('application/json');
        if (isJson) {
            const body = await res.json().catch(() => null) as ApiErrorBody | null;
            throw new ApiError(res.status, body?.error?.code || 'HttpError', body?.error?.message || res.statusText);
        }
        throw new ApiError(res.status, 'HttpError', res.statusText);
    }
    const cd = res.headers.get('content-disposition') || '';
    const m  = cd.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: m ? m[1] : null };
}
